import { useRef, useEffect } from 'react';
import { preampError } from '../../utils/cohesionEvents.js';
import { clientSide } from '../../utils/dom.js';

/**
 * useCohesionTimeout
 * Sets up a MutationObserver that triggers when the Cohesion snippet timeout updates the document's class list (if a decision is not returned).
 * This essentially acts like an event listener on Cohesion's timeout function.
 * We could set our own timeout, but would risk our error behavior running out of sync with Cohesion if the default timeout is adjusted (Frontier's Cohesion bundle changes the default timing, for example).
 *
 * @func
 * @category Hooks
 * @param {preampDecisions} preampDecisions - The preampDecisions state to check for changes.
 * @param {setPreampDecisions} setPreampDecisions - The state setter function to trigger rendering on cohesion failure.
 * @returns {void}
 */
const useCohesionTimeout = (preampDecisions, setPreampDecisions) => {
  // Preamp Decision can update multiple times, ex on a page change, and this timeout should only be set once.
  // the ref is used to track if the timeout has already been set.
  const ref = useRef(null);
  useEffect(() => {
    if (ref.current) return;
    ref.current = true;
    let observer;
    if (!preampDecisions) {
      observer = new MutationObserver(mutationsList => {
        for (const mutation of mutationsList) {
          if (mutation.target instanceof HTMLElement && mutation.attributeName === 'class' && !mutation.target.classList.contains('preampjs')) {
            preampError(setPreampDecisions)(new Error(`snippet timeout`));
            observer?.disconnect();
          }
        }
      });
      observer.observe(document.documentElement, {
        attributes: true,
        attributeFilter: ['class']
      });
      if (clientSide && window.cohesion) {
        window.cohesion('preamp:decisionsReceived', () => {
          observer?.disconnect();
        });
      }
    }
    return () => {
      if (observer) observer.disconnect();
    };
  }, [preampDecisions]);
};

export { useCohesionTimeout };
